import Cookies from "js-cookie";

// Marketing attribution captured from the landing URL and HubSpot's tracking
// cookie, forwarded to the hub with the cart so the backend can attach click
// data (Bing/Google) to the HubSpot deal and tie the contact to its analytics
// session via hubspotutk. Mirrors the existing affiliate-id capture pattern.
export type Tracking = {
  hubspotutk?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
  utm_id?: string;
  gclid?: string;
  gbraid?: string;
  wbraid?: string;
  msclkid?: string;
  fbclid?: string;
  ga_client_id?: string;
  landing_url?: string;
  page_url?: string;
};

// Attribution params we read from the URL and persist. hubspotutk is NOT here —
// it is read live from HubSpot's own cookie on each call.
//
// gbraid / wbraid are the important additions: since iOS 14's ATT changes
// Google Ads sends these INSTEAD of gclid for a large and growing share of iOS
// traffic, and iOS is the majority of this site's visitors. Those clicks
// previously arrived with nothing capturable, so the resulting bookings could
// never be attributed back to the campaign that paid for them.
//
// Keep in sync with js/sjd-tracking-capture.js and sjd_booking_tracking_keys()
// in the hub plugin — a key missing from any of the three is silently dropped.
const URL_PARAMS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
  "utm_id",
  "gclid",
  "gbraid",
  "wbraid",
  "msclkid",
  "fbclid",
] as const;

const TRACKING_COOKIE = "sjd_tracking";
const TRACKING_TTL_DAYS = 30;

// Key under which the first page of the visit is remembered. Stored in the same
// cookie as the URL params but written once (first-touch): it answers "which
// site/page did this visitor arrive on", which is what `source_site` reports on
// — overwriting it on later steps would make every booking look like it
// originated on the checkout page.
const LANDING_URL_KEY = "landing_url";

/**
 * Ad platforms drop their click ids into first-party cookies as well as the
 * URL, and the cookie outlives the URL: a visitor who lands on a tagged ad,
 * browses to three other pages, and only then opens the booking widget has lost
 * the query string but still carries the cookie. GTM's Conversion Linker and
 * Bing UET tags already write these on every page, so this reads what is
 * demonstrably there rather than adding tracking.
 *
 * URL params always win — the cookie is the fallback, valid ~90 days, and can
 * be stale from an earlier campaign.
 */
const parseGclAw = (raw?: string): string | undefined => {
  // `_gcl_aw` is `GCL.<timestamp>.<gclid>`. The gclid itself may contain dots,
  // so rejoin everything past the timestamp rather than taking the last piece.
  if (!raw) return undefined;
  const parts = raw.split(".");
  return parts.length >= 3 ? parts.slice(2).join(".") || undefined : undefined;
};

const parseGaClientId = (raw?: string): string | undefined => {
  // `_ga` is `GA1.<depth>.<clientId>` where clientId is itself `<rand>.<ts>`,
  // e.g. GA1.1.1234567890.1699999999 → "1234567890.1699999999".
  if (!raw) return undefined;
  const parts = raw.split(".");
  return parts.length >= 4 ? parts.slice(-2).join(".") : undefined;
};

const parseUetMsclkid = (raw?: string): string | undefined => {
  // Bing's UET tag normally stores the bare msclkid (a hex string, no dots).
  // Only unwrap when the value actually has the `<prefix>.<timestamp>.<value>`
  // shape — testing that the middle segment is numeric, so a msclkid that
  // happens to contain a dot is returned intact rather than truncated.
  if (!raw) return undefined;
  const parts = raw.split(".");
  if (parts.length >= 3 && /^\d+$/.test(parts[1])) {
    return parts.slice(2).join(".") || undefined;
  }
  return raw || undefined;
};

// Cookie-derived fallbacks, applied only where the URL gave us nothing.
const cookieFallbacks = (): Record<string, string | undefined> => ({
  gclid: parseGclAw(Cookies.get("_gcl_aw")),
  msclkid: parseUetMsclkid(Cookies.get("_uetmsclkid")),
  ga_client_id: parseGaClientId(Cookies.get("_ga")),
});

// `corrupt` tells the caller the cookie exists but could not be parsed, so it
// can overwrite it. Without that signal a single unreadable cookie value
// poisoned attribution for the full 30-day TTL: the parse failed, {} was
// returned, `changed` stayed false when the URL carried no new params, and the
// write below was skipped because the cookie was present-but-garbage rather
// than absent. Every subsequent visit repeated the same dance.
const readStoredParams = (): {
  params: Record<string, string>;
  corrupt: boolean;
} => {
  const raw = Cookies.get(TRACKING_COOKIE);
  if (!raw) return { params: {}, corrupt: false };
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object"
      ? { params: parsed as Record<string, string>, corrupt: false }
      : { params: {}, corrupt: true };
  } catch {
    return { params: {}, corrupt: true };
  }
};

// Capture attribution params from the current URL, merge them over anything
// previously stored (last-touch: a fresh non-empty value wins), and persist the
// result so it survives multi-step navigation and reloads within the funnel —
// including the common case where the user lands on a marketing page carrying
// the UTMs and only later opens the booking widget.
export const captureTrackingParams = (): Record<string, string> => {
  const { params: merged, corrupt } = readStoredParams();
  const search = new URLSearchParams(window.location.search);

  let changed = false;
  for (const key of URL_PARAMS) {
    const value = search.get(key)?.trim();
    if (value && merged[key] !== value) {
      merged[key] = value;
      changed = true;
    }
  }

  // Fill click ids from their first-party cookies only where the URL and any
  // previous capture came up empty, so a live ?gclid= always outranks a stale
  // 90-day cookie from an earlier campaign.
  for (const [key, value] of Object.entries(cookieFallbacks())) {
    if (value && !merged[key]) {
      merged[key] = value;
      changed = true;
    }
  }

  // First-touch only — see LANDING_URL_KEY.
  if (!merged[LANDING_URL_KEY]) {
    merged[LANDING_URL_KEY] = window.location.href;
    changed = true;
  }

  if (changed || corrupt || Cookies.get(TRACKING_COOKIE) === undefined) {
    try {
      Cookies.set(TRACKING_COOKIE, JSON.stringify(merged), {
        expires: TRACKING_TTL_DAYS,
      });
    } catch {
      // Ignore storage errors (private mode / quota).
    }
  }

  return merged;
};

// HubSpot's tracking script sets the `hubspotutk` cookie site-wide (not
// httpOnly, so JS-readable). May be absent if the script hasn't loaded or is
// blocked by the visitor.
export const getHubspotUtk = (): string | undefined =>
  Cookies.get("hubspotutk") || undefined;

// Full tracking payload: persisted URL params, cookie-derived ids, the landing
// page, and the live HubSpot cookie. Only non-empty fields are included.
export const getTracking = (): Tracking => {
  const params = captureTrackingParams();
  const tracking: Tracking = {};
  for (const key of URL_PARAMS) {
    if (params[key]) tracking[key] = params[key];
  }
  if (params.ga_client_id) tracking.ga_client_id = params.ga_client_id;
  if (params[LANDING_URL_KEY]) tracking.landing_url = params[LANDING_URL_KEY];

  // Current page, not persisted — this is the page the submission happened on,
  // which is what HubSpot shows as the converting page.
  tracking.page_url = window.location.href;

  const hutk = getHubspotUtk();
  if (hutk) tracking.hubspotutk = hutk;
  return tracking;
};
